<template>
  <div ref="tableWrapperRef" class="base-table-wrapper">
    <div v-if="$slots['query-form'] || showPagination" class="table-toolbar">
      <div v-if="$slots['query-form']" class="query-form-wrapper">
        <slot name="query-form"/>
      </div>

      <div v-if="showPagination" class="pagination-wrapper">
        <el-pagination
            layout="prev, pager, next, sizes"
            small
            :current-page="page"
            :page-size="size"
            :total="total"
            :page-sizes="pageSizes"
            @update:current-page="onPageChange"
            @update:page-size="onSizeChange"
        />
      </div>
    </div>

    <el-table
        ref="tableRef"
        :data="data"
        :border="border"
        class="table"
        :height="autoHeight"
        size="small"
        table-layout="fixed"
        highlight-current-row
        :row-key="rowKey"
        :row-style="rowStyle"
        @selection-change="handleSelectionChange"
        @sort-change="handleSort"
        @expand-change="handleExpandChange"
        @row-dblclick="handleRowDblClick"
        v-bind="$attrs"
        v-loading="loading"
    >
      <el-table-column
          v-if="showIndex"
          align="center"
          label="#"
          width="60"
      >
        <template #default="scope">
          <span>{{ (page - 1) * size + scope.$index + 1 }}</span>
        </template>
      </el-table-column>

      <el-table-column
          v-if="selectable"
          :selectable="rowSelectable"
          :reserve-selection="reserveSelection"
          type="selection"
          align="center"
          width="40"
      />

      <template v-for="col in internalColumns" :key="col.prop || col.label">
        <el-table-column
            v-if="col.type === 'expand'"
            type="expand"
            :width="col.width"
        >
          <template #default="scope">
            <slot name="expand" v-bind="scope"></slot>
          </template>
        </el-table-column>

        <el-table-column
            v-else-if="col.children"
            :align="col.align || 'left'"
            :header-align="col.headerAlign || col.align || 'left'"
        >
          <template #header>
            <slot v-if="col.headerSlot" :name="col.headerSlot" :col="col"></slot>
            <span v-else>
              {{ col.label }}
              <template v-if="col.value !== undefined">
                : {{ col.value }}
              </template>
            </span>
          </template>

          <template v-for="child in col.children" :key="child.prop || child.label">
            <el-table-column
                v-if="child.children"
                :align="child.align || 'left'"
                :header-align="child.headerAlign || child.align || 'left'"
                :fixed="child.fixed"
            >
              <template #header>
                <slot v-if="child.headerSlot" :name="child.headerSlot" :col="child"></slot>
                <span v-else>{{ child.label }}</span>
              </template>

              <el-table-column
                  v-for="grandchild in child.children"
                  :key="grandchild.prop"
                  :prop="grandchild.prop"
                  :label="grandchild.label"
                  :width="grandchild.width"
                  :min-width="grandchild.minWidth || 100"
                  :sortable="grandchild.sortable || false"
                  :sort-method="grandchild.sortMethod"
                  :filters="grandchild.filters"
                  :filter-method="grandchild.filterMethod"
                  :align="grandchild.align || 'left'"
                  :header-align="grandchild.headerAlign || grandchild.align || 'left'"
                  :fixed="grandchild.fixed"
              >
                <template v-if="grandchild.slot" #default="scope">
                  <slot :name="grandchild.slot" v-bind="scope" :col="grandchild"/>
                </template>
                <template v-else #default="scope">
                  {{ scope.row[grandchild.prop] }}
                </template>
              </el-table-column>
            </el-table-column>

            <el-table-column
                v-else
                :prop="child.prop"
                :label="child.label"
                :width="child.width"
                :min-width="child.minWidth || 100"
                :sortable="child.sortable || false"
                :sort-method="child.sortMethod"
                :filters="child.filters"
                :filter-method="child.filterMethod"
                :align="child.align || 'left'"
                :header-align="child.headerAlign || child.align || 'left'"
                :fixed="child.fixed"
            >
              <template v-if="child.slot" #default="scope">
                <slot :name="child.slot" v-bind="scope" :col="child"/>
              </template>
              <template v-else #default="scope">
                {{ scope.row[child.prop] }}
              </template>
            </el-table-column>
          </template>
        </el-table-column>

        <el-table-column
            v-else
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            :min-width="col.minWidth || 100"
            :sortable="col.sortable || false"
            :sort-method="col.sortMethod"
            :filters="col.filters"
            :filter-method="col.filterMethod"
            :align="col.align || 'left'"
            :header-align="col.headerAlign || col.align || 'left'"
            :fixed="col.fixed"
        >
          <template v-if="col.slot" #default="scope">
            <slot :name="col.slot" v-bind="scope" :col="col"/>
          </template>
          <template v-else #default="scope">
            {{ scope.row[col.prop] }}
          </template>
        </el-table-column>
      </template>

      <slot/>
    </el-table>

  </div>
</template>

<script lang="ts">
import {Options, Vue} from "vue-class-component";

@Options({
  props: {
    height: {
      type: [Number, String],
      default: null
    },
    tableKey: {
      type: String,
      required: false,
      default: ""
    },
    data: {
      type: Array,
      default: []
    },
    columns: {
      type: Array,
      default: []
    },
    rowKey: {
      type: String,
      default: "id"
    },
    rowHeight: {
      type: String,
      default: null,
    },
    naturalHeight: {
      type: Boolean,
      default: false
    },
    showIndex: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    reserveSelection: {
      type: Boolean,
      default: false
    },
    rowSelectable: {
      type: Function,
      default: () => true
    },
    showPagination: {
      type: Boolean,
      default: false
    },
    page: {
      type: Number,
      default: 1
    },
    size: {
      type: Number,
      default: 20,
    },
    total: {
      type: Number,
      default: 0
    },
    pageSizes: {
      type: Array,
      default: () => [20, 40, 60, 80, 100]
    },
    border: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tableRef: null,
      autoTableHeight: null,
      resizeObserver: null,
      internalColumns: this.columns
    }
  },
  mounted() {
    this.updateAutoHeight();
    window.addEventListener("resize", this.updateAutoHeight);

    if ((window as any).ResizeObserver) {
      const wrapper = this.$refs.tableWrapperRef as HTMLElement;
      this.resizeObserver = new ResizeObserver(() => this.updateAutoHeight());
      this.resizeObserver.observe(wrapper);
      this.resizeObserver.observe(document.body);
      if (wrapper.parentElement) {
        this.resizeObserver.observe(wrapper.parentElement);
      }
    }
  },
  updated() {
    this.updateAutoHeight();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateAutoHeight);
    this.resizeObserver?.disconnect();
  },
  watch: {
    data() {
      this.scheduleLayout();
    },
    columns: {
      handler(newVal) {
        this.internalColumns = newVal;
        this.scheduleLayout();
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    autoHeight() {
      if (this.naturalHeight) {
        return null;
      }

      return this.autoTableHeight || this.height || null;
    }
  },
  methods: {
    updateAutoHeight() {
      if (this.naturalHeight) {
        this.autoTableHeight = null;
        this.scheduleLayout();
        return;
      }

      if (typeof this.height === 'string') {
        this.autoTableHeight = null;
        this.scheduleLayout();
        return;
      }

      const wrapper = this.$refs.tableWrapperRef as HTMLElement;
      if (!wrapper) return;

      const table = wrapper.querySelector(".table") as HTMLElement;
      const tableTop = table?.getBoundingClientRect().top ?? wrapper.getBoundingClientRect().top;
      const bottomGap = 20;
      const minHeight = 240;
      const availableHeight = window.innerHeight - tableTop - bottomGap;
      const remainingHeight = Math.max(minHeight, Math.floor(availableHeight));
      const nextHeight = this.height ? Math.min(this.height, remainingHeight) : remainingHeight;

      if (this.autoTableHeight === nextHeight) {
        this.scheduleLayout();
        return;
      }

      this.autoTableHeight = nextHeight;
      this.scheduleLayout();
    },
    scheduleLayout() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.tableRef?.doLayout();
        });
      });
    },
    rowStyle() {
      return { height: this.rowHeight }
    },
    handleExpandChange(row: any) {
      this.$emit("expand-change", row);
    },
    handleSelectionChange(selection: any) {
      this.$emit('selection-change', selection);
    },
    handleSort(column: any) {
      this.$emit("sort-change", column);
    },
    onPageChange(newPage: number) {
      this.$emit("update:page", newPage);
      this.$emit("page-change", newPage);
    },
    onSizeChange(newSize: number) {
      this.$emit("update:pageSize", newSize);
      this.$emit("page-size-change", newSize);
    },
    handleRowDblClick(row: any) {
      this.$emit('row-dblclick', row);
    },
  }
})
export default class BaseTable extends Vue {

}
</script>

<style scoped>
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  margin-bottom: 12px;
  overflow: hidden;
}

.query-form-wrapper {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
}

.query-form-wrapper :deep(.filter-panel) {
  margin-bottom: 0;
}

.query-form-wrapper :deep(.el-form-item) {
  margin-bottom: 0;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  justify-content: flex-end;
  margin-left: auto;
}

.table-toolbar .pagination-wrapper {
  padding-top: 0;
}

.table-toolbar :deep(.el-pagination) {
  gap: 2px;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.table-toolbar :deep(.el-pagination .btn-prev),
.table-toolbar :deep(.el-pagination .btn-next),
.table-toolbar :deep(.el-pager li) {
  min-width: 24px;
  height: 24px;
  margin: 0;
}

.table-toolbar :deep(.el-pagination__sizes) {
  margin: 0 0 0 4px;
}

.table-toolbar :deep(.el-pagination__sizes .el-select) {
  width: 100px !important;
}

.table-toolbar :deep(.el-pagination__sizes .el-select__wrapper) {
  min-height: 24px;
  padding: 0 8px;
}
</style>
